import React, { useState, useEffect } from 'react';
import { Cookie, X, Settings as SettingsIcon } from 'lucide-react';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    functional: true,
    analytics: false,
    performance: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      const timer = setTimeout(() => {
        setShowBanner(true);
        // Trigger CSS transition after mount
        requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const dismiss = () => {
    setVisible(false);
    setTimeout(() => { setShowBanner(false); setShowCustomize(false); }, 300);
  };

  const handleAcceptAll = () => {
    savePreferences({ necessary: true, functional: true, analytics: true, performance: true });
  };

  const handleRejectNonEssential = () => {
    savePreferences({ necessary: true, functional: false, analytics: false, performance: false });
  };

  const handleSaveCustom = () => {
    savePreferences(preferences);
  };

  const savePreferences = (prefs) => {
    const consentData = {
      preferences: prefs,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    localStorage.setItem('cookie_consent', JSON.stringify(consentData));
    Object.keys(prefs).forEach(key => {
      localStorage.setItem(`cookie_${key}`, prefs[key]);
    });
    dismiss();
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: prefs }));
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return;
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!showBanner) return null;

  return (
    <div
      className={`fixed bottom-4 right-4 z-[9999] transition-all duration-500 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
      style={{ maxWidth: '380px', width: 'calc(100vw - 32px)' }}
    >
      <div
        className="rounded-2xl border border-stone-700/40 overflow-hidden shadow-2xl"
        style={{
          background: 'linear-gradient(180deg, #1c1a17 0%, #151311 100%)',
          boxShadow: '0 16px 48px -8px rgba(0,0,0,0.6), 0 0 0 1px rgba(120,113,108,0.08)',
        }}
      >
        {!showCustomize ? (
          <div className="p-4">
            {/* Header */}
            <div className="flex items-start gap-3 mb-3">
              <div className="w-8 h-8 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center flex-shrink-0">
                <Cookie size={15} className="text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-[13px] font-semibold text-stone-200">Cookie Preferences</h3>
                <p className="text-[11px] text-stone-300 mt-1 leading-relaxed">
                  We use cookies for authentication and to improve your experience. No advertising or cross-site tracking.
                </p>
              </div>
              <button
                onClick={handleRejectNonEssential}
                className="p-1 rounded-lg text-stone-400 hover:text-stone-200 hover:bg-stone-800/50 transition-colors flex-shrink-0"
                aria-label="Close"
              >
                <X size={14} />
              </button>
            </div>

            {/* Links */}
            <p className="text-[10px] text-stone-400 mb-3">
              <a href="/legal?doc=cookies" target="_blank" rel="noopener noreferrer" className="text-teal-400 hover:text-teal-300 transition-colors">Cookie Policy</a>
              {' · '}
              <a href="/legal?doc=privacy" target="_blank" rel="noopener noreferrer" className="text-teal-400 hover:text-teal-300 transition-colors">Privacy Policy</a>
            </p>

            {/* Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleAcceptAll}
                className="flex-1 px-3 py-2 rounded-xl text-[12px] font-medium bg-teal-500/15 text-teal-400 border border-teal-500/25 hover:bg-teal-500/25 transition-colors"
              >
                Accept All
              </button>
              <button
                onClick={handleRejectNonEssential}
                className="flex-1 px-3 py-2 rounded-xl text-[12px] font-medium text-stone-300 bg-stone-800/60 border border-stone-700/30 hover:bg-stone-700/60 transition-colors"
              >
                Essential Only
              </button>
              <button
                onClick={() => setShowCustomize(true)}
                className="p-2 rounded-xl text-stone-300 hover:text-stone-100 bg-stone-800/60 border border-stone-700/30 hover:bg-stone-700/60 transition-colors"
                title="Customize preferences"
              >
                <SettingsIcon size={14} />
              </button>
            </div>
          </div>
        ) : (
          <div className="p-4">
            {/* Customize header */}
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[13px] font-semibold text-stone-200">Customize Cookies</h3>
              <button
                onClick={() => setShowCustomize(false)}
                className="p-1 rounded-lg text-stone-400 hover:text-stone-200 hover:bg-stone-800/50 transition-colors"
                aria-label="Back"
              >
                <X size={14} />
              </button>
            </div>

            <div className="space-y-2 mb-3">
              {/* Necessary */}
              <div className="flex items-center justify-between py-2 px-3 rounded-xl bg-stone-800/30 border border-stone-700/20">
                <div>
                  <div className="text-[11px] font-medium text-stone-200">Necessary</div>
                  <div className="text-[10px] text-stone-400">Auth, security, core functionality</div>
                </div>
                <span className="text-[9px] font-medium text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">Always on</span>
              </div>

              <CookieToggle label="Functional" desc="Preferences, theme, sidebar state" enabled={preferences.functional} onChange={() => togglePreference('functional')} />
              <CookieToggle label="Analytics" desc="Anonymous usage patterns" enabled={preferences.analytics} onChange={() => togglePreference('analytics')} />
              <CookieToggle label="Performance" desc="Performance monitoring" enabled={preferences.performance} onChange={() => togglePreference('performance')} />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleSaveCustom}
                className="flex-1 px-3 py-2 rounded-xl text-[12px] font-medium bg-teal-500/15 text-teal-400 border border-teal-500/25 hover:bg-teal-500/25 transition-colors"
              >
                Save Preferences
              </button>
              <button
                onClick={() => setShowCustomize(false)}
                className="px-3 py-2 rounded-xl text-[12px] font-medium text-stone-300 bg-stone-800/60 border border-stone-700/30 hover:bg-stone-700/60 transition-colors"
              >
                Back
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CookieToggle = ({ label, desc, enabled, onChange }) => (
  <div className="flex items-center justify-between py-2 px-3 rounded-xl bg-stone-800/30 border border-stone-700/20">
    <div>
      <div className="text-[11px] font-medium text-stone-200">{label}</div>
      <div className="text-[10px] text-stone-400">{desc}</div>
    </div>
    <button
      onClick={onChange}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none ${
        enabled ? 'bg-teal-500/40' : 'bg-stone-700/60'
      }`}
      role="switch"
      aria-checked={enabled}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full transition-transform duration-200 ${
          enabled ? 'translate-x-[18px] bg-teal-400' : 'translate-x-[3px] bg-stone-400'
        }`}
      />
    </button>
  </div>
);

export default CookieConsent;
